import Clear from "@mui/icons-material/Clear";
import { MenuItem, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import type { Option } from "../constants";
import BaseForm from "./BaseForm";
import { CanvasFormTextField } from "./styled/CanvasFormTextField.styled";

export interface ClearReactionFormState {
	clearReactionName: string;
	reactionNodeId: string;
	reactionNodeName: string;
}

export interface ClearReactionFormProps {
	open: boolean;
	onClose: () => void;
	onSave: (state: ClearReactionFormState) => void;
	initialState?: Partial<ClearReactionFormState>;
	isSaving?: boolean;
	reactionOptions?: Option[];
	getDuplicateNameError?: (name: string) => string | undefined;
}

const ClearReactionForm = (props: ClearReactionFormProps) => {
	const {
		open,
		onClose,
		onSave,
		initialState,
		isSaving = false,
		reactionOptions = [],
		getDuplicateNameError,
	} = props;

	const initialFormState = useMemo(
		(): ClearReactionFormState => ({
			clearReactionName: initialState?.clearReactionName ?? "",
			reactionNodeId: initialState?.reactionNodeId ?? "",
			reactionNodeName: initialState?.reactionNodeName ?? "",
		}),
		[initialState],
	);

	const [formState, setFormState] = useState<ClearReactionFormState>(initialFormState);

	useEffect(() => {
		if (open) {
			setFormState(initialFormState);
		}
	}, [initialFormState, open]);

	const duplicateNameError = useMemo(
		() => getDuplicateNameError?.(formState.clearReactionName),
		[getDuplicateNameError, formState.clearReactionName],
	);

	const isSaveDisabled =
		!formState.clearReactionName.trim() ||
		Boolean(duplicateNameError) ||
		!formState.reactionNodeId;

	const handleSave = () => {
		onSave({
			...formState,
			clearReactionName: formState.clearReactionName.trim(),
		});
	};

	return (
		<BaseForm
			open={open}
			onClose={onClose}
			onSave={handleSave}
			title="Clear Reaction"
			icon={<Clear />}
			iconColor="#D32E26"
			gradient="linear-gradient(135deg, #E94136 0%, #B81E15 100%)"
			isSaving={isSaving}
			isSaveDisabled={isSaveDisabled}
			saveButtonText="Save Clear Reaction"
		>
			<Stack spacing={3}>
				<CanvasFormTextField
					fullWidth
					label="Name"
					value={formState.clearReactionName}
					error={Boolean(duplicateNameError)}
					helperText={duplicateNameError ?? ""}
					onChange={(event) =>
						setFormState((previous) => ({
							...previous,
							clearReactionName: event.target.value,
						}))
					}
					width="100%"
				/>
				<CanvasFormTextField
					select
					fullWidth
					label="Reaction to Clear"
					value={formState.reactionNodeId}
					onChange={(event) => {
						const selectedId = event.target.value;
						const selected = reactionOptions.find((o) => o.value === selectedId);
						setFormState((previous) => ({
							...previous,
							reactionNodeId: selectedId,
							reactionNodeName: selected?.label ?? "",
						}));
					}}
					width="100%"
				>
					{reactionOptions.length === 0 ? (
						<MenuItem disabled value="">
							<Typography variant="body2" color="text.secondary">
								No reactions available
							</Typography>
						</MenuItem>
					) : (
						reactionOptions.map((option) => (
							<MenuItem key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						))
					)}
				</CanvasFormTextField>
			</Stack>
		</BaseForm>
	);
};

export default ClearReactionForm;
